/**
 * TrainerExtranetDialog — Vague 99.5
 *
 * Admin-side panel to manage a freelance trainer's:
 *  - Lifecycle (draft | invited | active | suspended | archived) + invitation
 *  - Extranet ACL (tabs + granular perms + session scope)
 *  - Competency matrix (programs / categories with level)
 *  - Access log (last 100 entries)
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Loader2, Mail, ShieldCheck, ShieldAlert, Archive, Send, Save,
  CheckCircle2, Eye, EyeOff, Award, Trash2, Plus, Activity, Key, X,
  User as UserIcon, Edit3, Download, FileText, Phone, MapPin as MapPinIcon, Calendar, Upload,
} from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';

const LIFECYCLE_META = {
  draft:     { label: 'Brouillon',  bg: '#F3F4F8', fg: '#6B6C85' },
  invited:   { label: 'Invité',     bg: '#FFF7E6', fg: '#92400E' },
  active:    { label: 'Actif',      bg: '#E6FBF7', fg: '#0F766E' },
  suspended: { label: 'Suspendu',   bg: '#FEE2E2', fg: '#B91C1C' },
  archived:  { label: 'Archivé',    bg: '#F3F4F8', fg: '#6B6C85' },
};

const TAB_DEFS = [
  { key: 'home', label: 'Accueil' },
  { key: 'sessions', label: 'Sessions' },
  { key: 'emargements', label: 'Émargements' },
  { key: 'evaluations', label: 'Évaluations' },
  { key: 'documents', label: 'Documents' },
  { key: 'profil', label: 'Profil' },
];

const CONTACT_MODES = [
  { value: 'full', label: 'Complets' },
  { value: 'masked', label: 'Masqués partiellement' },
  { value: 'hidden', label: 'Cachés' },
];

const SESSION_SCOPES = [
  { value: 'auto', label: 'Auto (sessions où son nom apparaît)' },
  { value: 'manual', label: 'Manuelle (admin sélectionne)' },
];

export default function TrainerExtranetDialog({ open, onOpenChange, trainer, onChange, initialTab = 'profil', onEdit, onDelete, onSendContract, onDownloadContract, onDownloadIcs }) {
  const [tab, setTab] = useState(initialTab);
  const [acl, setAcl] = useState(null);
  const [allSessions, setAllSessions] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [accessLog, setAccessLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pwDialog, setPwDialog] = useState(false);
  const [manualPw, setManualPw] = useState('');
  const [manualPw2, setManualPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [trainerDocs, setTrainerDocs] = useState([]);

  useEffect(() => {
    if (!open || !trainer?.id) return;
    setTab(initialTab);
    api.get(`/trainers/${trainer.id}/acl`).then(({ data }) => setAcl(data.acl)).catch(() => {});
    api.get('/sessions').then(({ data }) => setAllSessions(data || [])).catch(() => {});
    api.get(`/trainers/${trainer.id}/competencies`).then(({ data }) => setCompetencies(data || [])).catch(() => {});
    api.get('/programs').then(({ data }) => setAllPrograms(data || [])).catch(() => {});
    api.get(`/trainers/${trainer.id}/access-log?limit=100`).then(({ data }) => setAccessLog(data || [])).catch(() => {});
    api.get(`/trainers/${trainer.id}/documents`).then(({ data }) => setTrainerDocs(data || [])).catch(() => {});
  }, [open, trainer?.id, initialTab]);

  const lifecycle = trainer?.lifecycle_status || 'draft';

  const setLifecycle = async (status) => {
    setSaving(true);
    try {
      await api.put(`/trainers/${trainer.id}/lifecycle`, { status });
      toast({ title: 'Statut mis à jour', description: LIFECYCLE_META[status]?.label });
      onChange?.();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' });
    } finally { setSaving(false); }
  };

  const sendInvitation = async () => {
    setSaving(true);
    try {
      const return_url = window.location.origin;
      const { data } = await api.post(`/trainers/${trainer.id}/invite`, { return_url });
      // La route répondait « sent: true » quoi qu'il arrive ; elle dit
      // maintenant la vérité, et l'écran la relaie plutôt que de féliciter.
      toast({
        title: data?.sent === false ? "L'invitation n'est pas partie" : 'Invitation envoyée',
        description: data?.sent === false
          ? (data?.error || `${trainer.email} — raison inconnue, voir le journal.`)
          : trainer.email,
      });
      onChange?.();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' });
    } finally { setSaving(false); }
  };

  const sendPasswordResetEmail = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/trainers/${trainer.id}/password-reset/email`);
      toast({ title: 'Email envoyé', description: data?.delivered ? 'Lien de réinitialisation envoyé' : 'Email généré (voir logs)' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' });
    } finally { setSaving(false); }
  };

  const applyManualPassword = async () => {
    if (manualPw.length < 8) { toast({ title: 'Minimum 8 caractères' }); return; }
    if (manualPw !== manualPw2) { toast({ title: 'Les mots de passe ne correspondent pas' }); return; }
    setSaving(true);
    try {
      await api.post(`/trainers/${trainer.id}/password-reset/manual`, { password: manualPw });
      toast({ title: 'Mot de passe mis à jour', description: 'Toutes les sessions actives ont été invalidées.' });
      setPwDialog(false);
      setManualPw(''); setManualPw2('');
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' });
    } finally { setSaving(false); }
  };

  const saveAcl = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/trainers/${trainer.id}/acl`, acl);
      setAcl(data.acl);
      toast({ title: 'Permissions enregistrées' });
      onChange?.();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' });
    } finally { setSaving(false); }
  };

  const addCompetency = async (target_type, target_id, target_label, level) => {
    try {
      const { data } = await api.post(`/trainers/${trainer.id}/competencies`, { target_type, target_id, target_label, level });
      setCompetencies((p) => [...p.filter((c) => !(c.target_type === target_type && c.target_id === target_id)), data]);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' });
    }
  };

  const removeCompetency = async (compId) => {
    try {
      await api.delete(`/trainers/${trainer.id}/competencies/${compId}`);
      setCompetencies((p) => p.filter((c) => c.id !== compId));
    } catch (e) {
      toast({ title: 'Erreur' });
    }
  };

  if (!trainer) return null;
  const meta = LIFECYCLE_META[lifecycle] || LIFECYCLE_META.draft;
  // Vague 112 — internal trainers linked to a team user share their admin
  // credentials (managed in Paramètres → Équipe). The Cycle de vie actions
  // (invitation, password reset, suspend/archive) are therefore disabled
  // for them to prevent identity drift.
  const isManagedByTeam = !!trainer?.is_internal && !!trainer?.linked_user_id;
  const managedHint = isManagedByTeam
    ? "Formateur interne — identifiants gérés via Paramètres → Équipe (mêmes accès que son compte OFManager)."
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[820px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[18px] flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <ShieldCheck className="w-5 h-5 text-[#4F46E5]" /> Extranet · {trainer.name}
            <span className="ml-2 px-2 h-5 rounded text-[10.5px] font-bold uppercase tracking-wide flex items-center" style={{ background: meta.bg, color: meta.fg }} data-testid="trainer-lifecycle-badge">{meta.label}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-[#EFF0F6] -mt-2 mb-3 overflow-x-auto">
          {[
            { id: 'profil', label: 'Profil', icon: UserIcon },
            { id: 'lifecycle', label: 'Cycle de vie', icon: Send },
            { id: 'acl', label: 'Permissions', icon: ShieldCheck },
            { id: 'competencies', label: 'Compétences', icon: Award },
            { id: 'audit', label: 'Audit', icon: Activity },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`trainer-extranet-tab-${t.id}`}
              className={`px-3 py-2 text-[12.5px] font-semibold flex items-center gap-1.5 border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-[#6B6C85] hover:text-[#1A1B2E]'}`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        <div className="max-h-[70vh] overflow-y-auto pr-1">
          {tab === 'profil' && (
            <div className="space-y-4" data-testid="trainer-extranet-profil">
              {/* Quick actions */}
              <div className="flex flex-wrap gap-2">
                {onEdit && (
                  <button onClick={() => onEdit(trainer)} data-testid="trainer-profil-edit"
                          className="h-9 px-3 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#E0E7FF]">
                    <Edit3 className="w-3.5 h-3.5" /> Modifier la fiche
                  </button>
                )}
                {onSendContract && (
                  <button onClick={() => onSendContract(trainer)} data-testid="trainer-profil-contract"
                          className="h-9 px-3 rounded-lg bg-[#F0FDF4] text-[#166534] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#DCFCE7]">
                    <Send className="w-3.5 h-3.5" /> {trainer.active_contract ? 'Renvoyer contrat' : 'Envoyer contrat'}
                  </button>
                )}
                {onDownloadIcs && (
                  <button onClick={() => onDownloadIcs(trainer)} data-testid="trainer-profil-ics"
                          className="h-9 px-3 rounded-lg border border-[#EFF0F6] text-[#4A4B63] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#FAFAFE]">
                    <Calendar className="w-3.5 h-3.5" /> Planning .ics
                  </button>
                )}
                {onDelete && (
                  <button onClick={() => onDelete(trainer)} data-testid="trainer-profil-delete"
                          className="h-9 px-3 rounded-lg border border-[#FECACA] text-[#B91C1C] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#FEF2F2] ml-auto">
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </button>
                )}
              </div>

              {/* Identity */}
              <div className="rounded-xl border border-[#EFF0F6] p-3.5 bg-[#FAFAFE]">
                <div className="flex items-start gap-3">
                  {trainer.avatar && <img src={trainer.avatar} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Satoshi','Plus Jakarta Sans',sans-serif", letterSpacing: '-0.02em' }}>{trainer.name}</div>
                    {trainer.speciality && <div className="text-[12.5px] text-[#6B6C85]">{trainer.speciality}</div>}
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {trainer.status && <span className={`text-[10.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${trainer.status === 'Interne' ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'bg-[#E6FBF7] text-[#0F766E]'}`}>{trainer.status}</span>}
                      {trainer.domain && <span className="text-[10.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#FFF7ED] text-[#C2410C]">{trainer.domain}</span>}
                      {trainer.availability_status && (
                        <span className={`text-[10.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                          trainer.availability_status === 'available' ? 'bg-[#E6FBF7] text-[#0F766E]' :
                          trainer.availability_status === 'busy' ? 'bg-[#FFF7E6] text-[#92400E]' : 'bg-[#FEE2E2] text-[#B91C1C]'
                        }`}>
                          {trainer.availability_status === 'available' ? '🟢 Disponible' : trainer.availability_status === 'busy' ? '🟡 Occupé' : '🔴 Indisponible'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <InfoRow icon={Mail}       label="Email"    value={trainer.email} />
                <InfoRow icon={Phone}      label="Téléphone" value={trainer.phone} />
                <InfoRow icon={ShieldCheck} label="SIRET"    value={trainer.siret} />
                <InfoRow icon={FileText}   label="NDA"       value={trainer.nda} />
                <InfoRow icon={FileText}   label="Forme juridique" value={trainer.legal_form} />
                <InfoRow icon={MapPinIcon} label="Adresse"  value={[trainer.address, trainer.postal_code, trainer.city].filter(Boolean).join(', ')} />
                {(trainer.daily_rate_eur || trainer.hourly_rate_eur) && (
                  <InfoRow icon={Award} label="Tarifs"
                           value={[trainer.daily_rate_eur && `${trainer.daily_rate_eur} €/j`, trainer.hourly_rate_eur && `${trainer.hourly_rate_eur} €/h`].filter(Boolean).join(' · ')} />
                )}
                {trainer.rc_pro?.insurer && (
                  <InfoRow icon={ShieldCheck} label="RC Pro"
                           value={`${trainer.rc_pro.insurer} · ${trainer.rc_pro.policy || '—'}${trainer.rc_pro.expires_at ? ` · exp. ${trainer.rc_pro.expires_at}` : ''}`} />
                )}
              </div>

              {/* Active contract */}
              {trainer.active_contract && (
                <div className="rounded-xl border border-[#EFF0F6] p-3.5 bg-white" data-testid="trainer-profil-contract-block">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[12.5px] font-semibold text-[#1A1B2E]">Contrat de sous-traitance</div>
                    <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded ${trainer.active_contract.status === 'Signé' ? 'bg-[#E6FBF7] text-[#0F766E]' : 'bg-[#FEF3C7] text-[#92400E]'}`}>
                      {trainer.active_contract.status}
                    </span>
                  </div>
                  {trainer.active_contract.mission_description && (
                    <div className="text-[12px] text-[#6B6C85] mb-2">{trainer.active_contract.mission_description}</div>
                  )}
                  {trainer.active_contract.signed_at && (
                    <div className="text-[11.5px] text-[#0F766E] mb-2">Signé par {trainer.active_contract.signatory_name} le {new Date(trainer.active_contract.signed_at).toLocaleDateString('fr-FR')}</div>
                  )}
                  {onDownloadContract && (
                    <button onClick={() => onDownloadContract(trainer)} data-testid="trainer-profil-contract-download"
                            className="w-full h-9 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#E0E7FF]">
                      <Download className="w-3.5 h-3.5" /> Télécharger le PDF
                    </button>
                  )}
                </div>
              )}

              {/* Documents (read-only listing + download) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-[#4F46E5]">Documents ({trainerDocs.length})</div>
                </div>
                {trainerDocs.length === 0 ? (
                  <div className="text-[12px] italic text-[#8E8FA5]">Aucun document. Les pièces (CV, diplômes, RIB, Kbis) se gèrent via la fiche.</div>
                ) : (
                  <ul className="divide-y divide-[#F3F4F8] border border-[#EFF0F6] rounded-xl">
                    {trainerDocs.map((d) => (
                      <li key={d.id} className="flex items-center gap-2 py-2 px-3" data-testid={`trainer-profil-doc-${d.id}`}>
                        <FileText className="w-3.5 h-3.5 text-[#4F46E5] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{d.filename}</div>
                          <div className="text-[10.5px] text-[#8E8FA5]">{d.type} · {new Date(d.uploaded_at || d.created_at).toLocaleDateString('fr-FR')}</div>
                        </div>
                        <a href={`/api/trainers/${trainer.id}/documents/${d.id}/download`}
                           target="_blank" rel="noreferrer"
                           className="p-1.5 rounded hover:bg-[#F3F4F8] text-[#4F46E5]" title="Télécharger">
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {tab === 'lifecycle' && (
            <div className="space-y-4" data-testid="trainer-extranet-lifecycle">
              <div className="text-[12.5px] text-[#4A4B63]">
                Le cycle de vie contrôle l'accès du formateur à son extranet.
              </div>
              {isManagedByTeam && (
                <div className="rounded-xl border border-[#C7D2FE] bg-[#EEF2FF] px-3 py-2.5 flex items-start gap-2" data-testid="trainer-lifecycle-managed-banner">
                  <ShieldCheck className="w-4 h-4 text-[#4F46E5] shrink-0 mt-0.5" />
                  <div className="text-[12px] text-[#3730A3] leading-snug">
                    <strong className="font-semibold">Formateur interne géré par l'Équipe.</strong>{' '}
                    Les identifiants et la réinitialisation du mot de passe se gèrent depuis{' '}
                    <strong>Paramètres → Équipe</strong> (mêmes accès que son compte OFManager).
                  </div>
                </div>
              )}
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#4F46E5] mb-2">Accès & invitation</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <ActionCard
                    icon={Mail} color="#4F46E5" title="Envoyer une invitation"
                    desc={`Email d'activation valable 24h vers ${trainer.email || '—'}`}
                    onClick={sendInvitation} disabled={!trainer.email || saving || isManagedByTeam}
                    disabledHint={isManagedByTeam ? managedHint : undefined}
                    testid="trainer-action-invite"
                  />
                  <ActionCard
                    icon={CheckCircle2} color="#0F766E" title="Activer"
                    desc="Le formateur peut accéder à son extranet"
                    onClick={() => setLifecycle('active')} disabled={lifecycle === 'active' || saving || isManagedByTeam}
                    disabledHint={isManagedByTeam ? managedHint : undefined}
                    testid="trainer-action-activate"
                  />
                  <ActionCard
                    icon={ShieldAlert} color="#B91C1C" title="Suspendre"
                    desc="Coupe immédiatement toutes les sessions actives"
                    onClick={() => setLifecycle('suspended')} disabled={lifecycle === 'suspended' || saving || isManagedByTeam}
                    disabledHint={isManagedByTeam ? managedHint : undefined}
                    testid="trainer-action-suspend"
                  />
                  <ActionCard
                    icon={Archive} color="#6B6C85" title="Archiver"
                    desc="Retire de la liste active, garde l'historique Qualiopi"
                    onClick={() => setLifecycle('archived')} disabled={lifecycle === 'archived' || saving || isManagedByTeam}
                    disabledHint={isManagedByTeam ? managedHint : undefined}
                    testid="trainer-action-archive"
                  />
                </div>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#4F46E5] mb-2">Mot de passe</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <ActionCard
                    icon={Mail} color="#4F46E5" title="Email de réinitialisation"
                    desc="Envoie un lien sécurisé au formateur pour choisir son mot de passe"
                    onClick={sendPasswordResetEmail} disabled={saving || isManagedByTeam}
                    disabledHint={isManagedByTeam ? managedHint : undefined}
                    testid="trainer-action-pw-email"
                  />
                  <ActionCard
                    icon={Key} color="#0F172A" title="Définir manuellement"
                    desc="Saisissez le mot de passe (à transmettre oralement)"
                    onClick={() => setPwDialog(true)} disabled={saving || isManagedByTeam}
                    disabledHint={isManagedByTeam ? managedHint : undefined}
                    testid="trainer-action-pw-manual"
                  />
                </div>
              </div>
            </div>
          )}

          {tab === 'acl' && (
            <div className="space-y-4" data-testid="trainer-extranet-acl">
              {!acl ? <div className="text-[12.5px] text-[#8E8FA5]"><Loader2 className="inline w-3 h-3 animate-spin mr-1" /> Chargement…</div> : (
                <>
                  <div className="rounded-xl border border-[#EFF0F6] p-3 flex items-center gap-3">
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold text-[#1A1B2E]">Accès extranet</div>
                      <div className="text-[12px] text-[#6B6C85]">Désactiver coupe l'accès et invalide la session en cours.</div>
                    </div>
                    <ToggleSw value={acl.enabled} onChange={(v) => setAcl({ ...acl, enabled: v })} testid="acl-enabled" />
                  </div>

                  <Section label="Onglets visibles">
                    {TAB_DEFS.map((t) => (
                      <ToggleRow
                        key={t.key} label={t.label}
                        value={!!acl.tabs?.[t.key]}
                        onChange={(v) => setAcl({ ...acl, tabs: { ...acl.tabs, [t.key]: v } })}
                        testid={`acl-tab-${t.key}`}
                      />
                    ))}
                  </Section>

                  <Section label="Permissions granulaires">
                    <SelectRow
                      label="Voir les coordonnées des apprenants" testid="acl-perm-contacts"
                      value={acl.perm?.view_learner_contacts || 'masked'}
                      options={CONTACT_MODES}
                      onChange={(v) => setAcl({ ...acl, perm: { ...acl.perm, view_learner_contacts: v } })}
                    />
                    <ToggleRow
                      label="Voir le détail des évaluations (sinon score global uniquement)"
                      value={acl.perm?.view_evaluations_detail !== false}
                      onChange={(v) => setAcl({ ...acl, perm: { ...acl.perm, view_evaluations_detail: v } })}
                      testid="acl-perm-evaluations"
                    />
                    <ToggleRow
                      label="Télécharger les PDFs (convocations / programmes / attestations)"
                      value={acl.perm?.download_pdfs !== false}
                      onChange={(v) => setAcl({ ...acl, perm: { ...acl.perm, download_pdfs: v } })}
                      testid="acl-perm-download"
                    />
                    <ToggleRow
                      label="Modifier ses propres documents"
                      value={acl.perm?.edit_own_documents !== false}
                      onChange={(v) => setAcl({ ...acl, perm: { ...acl.perm, edit_own_documents: v } })}
                      testid="acl-perm-edit-docs"
                    />
                    <ToggleRow
                      label="Voir les factures de l'OF"
                      value={!!acl.perm?.view_invoices}
                      onChange={(v) => setAcl({ ...acl, perm: { ...acl.perm, view_invoices: v } })}
                      testid="acl-perm-invoices"
                    />
                    <SelectRow
                      label="Périmètre des sessions visibles" testid="acl-perm-scope"
                      value={acl.perm?.session_scope || 'auto'}
                      options={SESSION_SCOPES}
                      onChange={(v) => setAcl({ ...acl, perm: { ...acl.perm, session_scope: v } })}
                    />
                  </Section>

                  {acl.perm?.session_scope === 'manual' && (
                    <Section label="Sessions autorisées (mode manuel)">
                      <div className="max-h-48 overflow-y-auto border border-[#EFF0F6] rounded-lg p-2">
                        {allSessions.length === 0 ? <div className="text-[12px] text-[#8E8FA5] italic p-2">Aucune session.</div> : allSessions.map((s) => {
                          const checked = (acl.manual_session_ids || []).includes(s.id);
                          return (
                            <label key={s.id} className="flex items-center gap-2 py-1 text-[12.5px] cursor-pointer hover:bg-[#FAFAFE] px-1 rounded">
                              <input
                                type="checkbox" checked={checked}
                                data-testid={`acl-session-${s.id}`}
                                onChange={(e) => {
                                  const set = new Set(acl.manual_session_ids || []);
                                  if (e.target.checked) set.add(s.id); else set.delete(s.id);
                                  setAcl({ ...acl, manual_session_ids: Array.from(set) });
                                }}
                              />
                              <span className="flex-1 truncate">{s.title || s.id}</span>
                              <span className="text-[10.5px] text-[#8E8FA5]">{s.startDate || ''}</span>
                            </label>
                          );
                        })}
                      </div>
                    </Section>
                  )}

                  <button onClick={saveAcl} disabled={saving} data-testid="trainer-acl-save"
                          className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 disabled:opacity-60">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Enregistrer les permissions
                  </button>
                </>
              )}
            </div>
          )}

          {tab === 'competencies' && (
            <div className="space-y-3" data-testid="trainer-extranet-competencies">
              <div className="text-[12.5px] text-[#4A4B63]">Associez ce formateur à des programmes ou catégories avec un niveau (junior / confirmé / expert).</div>
              {competencies.length === 0 ? (
                <div className="text-[12.5px] italic text-[#8E8FA5] py-2">Aucune compétence référencée.</div>
              ) : (
                <ul className="divide-y divide-[#F3F4F8] border border-[#EFF0F6] rounded-xl">
                  {competencies.map((c) => (
                    <li key={c.id} className="flex items-center gap-2 py-2 px-3" data-testid={`trainer-comp-${c.id}`}>
                      <Award className="w-3.5 h-3.5 text-[#4F46E5]" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{c.target_label || c.target_id}</div>
                        <div className="text-[10.5px] text-[#8E8FA5]">{c.target_type === 'program' ? 'Programme' : 'Catégorie'} · niveau {c.level}</div>
                      </div>
                      <button onClick={() => removeCompetency(c.id)} className="text-[#B91C1C] hover:bg-[#FEE2E2] p-1 rounded" data-testid={`trainer-comp-remove-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <AddCompetencyForm allPrograms={allPrograms} onAdd={addCompetency} />
            </div>
          )}

          {tab === 'audit' && (
            <div className="space-y-2" data-testid="trainer-extranet-audit">
              <div className="text-[12.5px] text-[#4A4B63]">100 dernières actions de ce formateur sur son extranet.</div>
              {accessLog.length === 0 ? (
                <div className="text-[12.5px] italic text-[#8E8FA5] py-2">Aucune activité enregistrée.</div>
              ) : (
                <ul className="divide-y divide-[#F3F4F8] border border-[#EFF0F6] rounded-xl text-[12px]">
                  {accessLog.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 py-1.5 px-3">
                      <span className="text-[10.5px] text-[#8E8FA5] w-32 shrink-0">{new Date(a.ts).toLocaleString('fr-FR')}</span>
                      <span className="font-semibold text-[#1A1B2E] flex-1">{a.action}</span>
                      {a.resource_id && <span className="text-[#8E8FA5] font-mono text-[11px]">{a.resource_type}/{a.resource_id}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Manual password sub-dialog */}
        {pwDialog && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center px-4" onClick={() => setPwDialog(false)} data-testid="manual-pw-overlay">
            <div className="bg-white rounded-2xl max-w-sm w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Key className="w-4 h-4 text-[#0F172A]" />
                  <h3 className="text-[15px] font-bold text-[#1A1B2E]">Définir le mot de passe</h3>
                </div>
                <button onClick={() => setPwDialog(false)} className="p-1 rounded hover:bg-[#F3F4F8]"><X className="w-4 h-4" /></button>
              </div>
              <p className="text-[12px] text-[#6B6C85] mb-3">
                Toutes les sessions actives du formateur seront invalidées. Transmettez-lui ce mot de passe par un canal sécurisé (téléphone, SMS).
              </p>
              <div className="space-y-2">
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'} value={manualPw} onChange={(e) => setManualPw(e.target.value)}
                    data-testid="manual-pw-input" placeholder="Nouveau mot de passe (8+ car.)"
                    className="w-full h-10 px-3 pr-10 rounded-xl border border-[#EFF0F6] bg-[#F6F7FB] focus:bg-white focus:border-[#4F46E5] outline-none text-[13px]" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#8E8FA5] hover:text-[#4F46E5]">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <input
                  type={showPw ? 'text' : 'password'} value={manualPw2} onChange={(e) => setManualPw2(e.target.value)}
                  data-testid="manual-pw-input2" placeholder="Confirmation"
                  className="w-full h-10 px-3 rounded-xl border border-[#EFF0F6] bg-[#F6F7FB] focus:bg-white focus:border-[#4F46E5] outline-none text-[13px]" />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setPwDialog(false)} className="h-9 px-3 rounded-lg border border-[#EFF0F6] text-[12.5px] font-semibold text-[#6B6C85]">Annuler</button>
                <button onClick={applyManualPassword} disabled={saving || !manualPw} data-testid="manual-pw-apply"
                        className="h-9 px-3 rounded-lg bg-[#4F46E5] text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-60">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Appliquer
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ActionCard({ icon: Icon, color, title, desc, onClick, disabled, testid, disabledHint }) {
  return (
    <button
      onClick={onClick} disabled={disabled} data-testid={testid}
      title={disabled && disabledHint ? disabledHint : undefined}
      className="text-left rounded-xl border border-[#EFF0F6] bg-white p-3.5 hover:border-[#C7D2FE] hover:bg-[#FAFAFE] transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[92px] flex flex-col items-start"
    >
      <div className="flex items-center gap-2 mb-1.5 w-full">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}14` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </span>
        <span className="text-[13px] font-semibold text-[#1A1B2E] leading-tight">{title}</span>
      </div>
      <div className="text-[11.5px] text-[#6B6C85] leading-snug">{desc}</div>
    </button>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg px-2.5 py-1.5 bg-[#FAFAFE] border border-[#EFF0F6]">
      <Icon className="w-3.5 h-3.5 text-[#4F46E5] shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#8E8FA5]">{label}</div>
        <div className="text-[12.5px] text-[#1A1B2E] break-words">{value}</div>
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wider text-[#4F46E5] mb-1.5">{label}</div>
      <div className="rounded-xl border border-[#EFF0F6] divide-y divide-[#F3F4F8]">{children}</div>
    </div>
  );
}

function ToggleRow({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <span className="text-[12.5px] text-[#1A1B2E]">{label}</span>
      <ToggleSw value={value} onChange={onChange} testid={testid} />
    </div>
  );
}

function SelectRow({ label, value, options, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <span className="text-[12.5px] text-[#1A1B2E]">{label}</span>
      <select
        value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px] focus:border-[#4F46E5] outline-none"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function ToggleSw({ value, onChange, testid }) {
  return (
    <button
      type="button" onClick={() => onChange(!value)} data-testid={testid}
      className={`w-11 h-6 rounded-full transition-colors relative ${value ? 'bg-[#4F46E5]' : 'bg-[#D1D5E0]'}`}
    >
      <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full transition-transform shadow-sm ${value ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
    </button>
  );
}

function AddCompetencyForm({ allPrograms, onAdd }) {
  const [target, setTarget] = useState('');
  const [level, setLevel] = useState('confirmed');
  const submit = () => {
    if (!target) return;
    const prog = allPrograms.find((p) => p.id === target);
    onAdd('program', target, prog?.name || prog?.title || target, level);
    setTarget('');
  };
  return (
    <div className="rounded-xl border border-[#EFF0F6] p-3 flex flex-wrap items-end gap-2 bg-[#FAFAFE]">
      <div className="flex-1 min-w-[180px]">
        <label className="text-[10.5px] font-bold uppercase text-[#8E8FA5] block mb-0.5">Programme</label>
        <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="trainer-comp-target"
                className="w-full h-9 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px]">
          <option value="">Sélectionner…</option>
          {allPrograms.map((p) => <option key={p.id} value={p.id}>{p.name || p.title || p.id}</option>)}
        </select>
      </div>
      <div className="w-32">
        <label className="text-[10.5px] font-bold uppercase text-[#8E8FA5] block mb-0.5">Niveau</label>
        <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="trainer-comp-level"
                className="w-full h-9 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px]">
          <option value="junior">Junior</option>
          <option value="confirmed">Confirmé</option>
          <option value="expert">Expert</option>
        </select>
      </div>
      <button onClick={submit} disabled={!target} data-testid="trainer-comp-add"
              className="h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1 disabled:opacity-50">
        <Plus className="w-3.5 h-3.5" /> Ajouter
      </button>
    </div>
  );
}
